<template>
  <!-- Only for a GM, and only worth showing while the loop is off: with
       auto_cycle_player driving the world every 45 seconds a manual step is
       noise, so the control states that it is idle rather than pretending to be
       the only clock.

       Levers only. The cycle and phase are not restated here — this mounts
       inside `CurrentTurnComponent`, which is already the page's one counter,
       and a second readout beside it drifted out of step with the first. -->
  <span
      v-if="isGameMaster"
      class="gm-clock"
      :class="{ 'gm-clock--auto': autoPlayEnabled }"
      :title="$t('gmClock.label')"
  >
    <button
        type="button"
        class="gm-clock__step"
        :disabled="busy"
        :title="$t('gmClock.nextPhaseHint')"
        @click="step('phase')"
    >
      {{ $t('gmClock.nextPhase') }}
    </button>
    <button
        type="button"
        class="gm-clock__step"
        :disabled="busy"
        :title="$t('gmClock.nextCycleHint')"
        @click="step('cycle')"
    >
      {{ $t('gmClock.nextCycle') }}
    </button>

    <!-- The fight clock is a separate clock, so it needs a separate lever. With
         a fight on screen this advances *that* fight; with none it runs one pass
         of the worker over every sealed fight that is owing something. -->
    <button
        type="button"
        class="gm-clock__step gm-clock__step--fight"
        :disabled="busy"
        :title="fightId ? $t('gmClock.nextFightPhaseHint') : $t('gmClock.tickFightsHint')"
        @click="step('fight')"
    >
      {{ fightId ? $t('gmClock.nextFightPhase') : $t('gmClock.tickFights') }}
    </button>

    <span v-if="autoPlayEnabled" class="gm-clock__auto-note">{{ $t('gmClock.autoOn') }}</span>
    <span v-if="error" class="gm-clock__error">{{ error }}</span>
  </span>
</template>

<script>
import {GameMasterCycleService} from '@/services/GameMasterCycleService';
import {GameMasterFightService} from '@/services/GameMasterFightService';

export default {
  name: 'GameMasterCycleControl',
  props: {
    /**
     * The fight on screen, when there is one. Given it, the fight lever
     * advances that fight specifically — a whole-clock tick cannot, because it
     * only looks at fights already past their deadline.
     */
    fightId: {
      type: String,
      default: null,
    },
  },
  emits: ['advanced'],
  data() {
    return {
      service: new GameMasterCycleService(),
      fightService: new GameMasterFightService(),
      autoPlayEnabled: false,
      busy: false,
      error: null,
    };
  },
  computed: {
    isGameMaster() {
      return !!this.$store?.getters?.isGameMaster;
    },
  },
  mounted() {
    if (this.isGameMaster) {
      this.refresh();
    }
  },
  methods: {
    async refresh() {
      try {
        const state = await this.service.getCurrent();
        this.autoPlayEnabled = !!state.auto_play_enabled;
        this.error = null;
      } catch (err) {
        // A GM with no resolvable campaign is the common case here, and it is
        // not worth an alarm — the control simply has nothing to show.
        console.warn('[GameMasterCycleControl] Could not read the world clock:', err);
      }
    },
    /**
     * Step the clock, then tell the parent so it can re-read whatever the step
     * changed. The response already carries where the clock landed, so this
     * does not need a second read to update itself.
     */
    async step(unit) {
      if (this.busy) return;
      this.busy = true;
      this.error = null;
      try {
        let result;
        if (unit === 'fight') {
          // The fight clock, not the world clock — it leaves the cycle where
          // it is, so the cycle readout is deliberately not touched here.
          result = this.fightId
              ? await this.fightService.advanceFightPhase(this.fightId)
              : await this.fightService.tickFightClock();
        } else {
          result = unit === 'cycle'
              ? await this.service.advanceCycle()
              : await this.service.advancePhase();
        }
        this.$emit('advanced', result);
      } catch (err) {
        this.error = err?.response?.data?.detail || this.$t('gmClock.failed');
        console.error('[GameMasterCycleControl] Advance failed:', err);
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
/* No box of its own. The control lives inside the cycle counter chip, so it
   borrows that chip's border, backdrop and font rather than stacking a second
   panel — and it speaks the player HUD's cyan, not the GM dock's gold, because
   this is player space. */
.gm-clock {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: 0.5rem;
  padding-left: 0.5rem;
  border-left: 1px solid rgba(0, 255, 255, 0.25);
  font-family: inherit;
  font-size: 0.55rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(0, 255, 255, 0.7);
}

/* Dimmed while the loop is driving the world: the buttons still work, they are
   just not the thing moving the game. */
.gm-clock--auto {
  opacity: 0.7;
}

.gm-clock__step {
  padding: 0.15rem 0.4rem;
  background: rgba(0, 255, 255, 0.08);
  border: 1px solid rgba(0, 255, 255, 0.3);
  border-radius: 0.2rem;
  color: inherit;
  font: inherit;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}

.gm-clock__step:hover:not(:disabled) {
  color: #00ffff;
  background: rgba(0, 255, 255, 0.16);
  border-color: rgba(0, 255, 255, 0.6);
}

.gm-clock__step:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* The fight clock is still a different clock and still marked as one — but in
   weight, not in hue. The amber this used to carry was the GM dock's gold, left
   behind when the control moved inside the player HUD's cycle counter chip, and
   it read as a leftover precisely because the button's text stayed cyan while
   only its border went gold. Lit cyan says "the other clock" without the border
   and the label disagreeing about which palette this control is in. */
.gm-clock__step--fight {
  background: rgba(0, 255, 255, 0.16);
  border-color: rgba(0, 255, 255, 0.55);
  color: rgba(0, 255, 255, 0.95);
}

.gm-clock__step--fight:hover:not(:disabled) {
  color: #00ffff;
  background: rgba(0, 255, 255, 0.24);
  border-color: #00ffff;
}

.gm-clock__auto-note,
.gm-clock__error {
  text-transform: none;
  letter-spacing: 0.02em;
}

.gm-clock__auto-note {
  color: rgba(0, 255, 255, 0.45);
}

.gm-clock__error {
  color: #e88a84;
}
</style>
